/**
 * Collection Vehicle Display - Main Orchestrator
 * Shows selected vehicle information in collection pages using modular architecture
 */
class CollectionVehicleDisplay {
  constructor() {
    this.dataManager = null;
    this.uiManager = null;
    this.urlManager = null;
    this.initialized = false;
    
    this.init();
  }

  /**
   * Initialize the collection vehicle display
   */
  init() {
    // Check if dependencies are loaded
    if (!this.checkDependencies()) {
      console.log('Collection Vehicle Display modules not loaded yet, deferring initialization...');
      setTimeout(() => this.init(), 100);
      return;
    }

    this.initializeModules();
    this.setupDisplay();
    this.attachEventListeners();

    this.initialized = true;
    console.log('🚀 Collection Vehicle Display initialized with modular architecture');

    // Check if we should auto-open modal after clear filter action
    this.checkAndOpenModalAfterClear();
  }

  /**
   * Check if all required modules are loaded
   */
  checkDependencies() {
    return typeof CollectionVehicleData !== 'undefined' &&
           typeof CollectionVehicleUI !== 'undefined' &&
           typeof CollectionVehicleURL !== 'undefined';
  }

  /**
   * Initialize module instances
   */
  initializeModules() {
    this.dataManager = new CollectionVehicleData();
    this.uiManager = new CollectionVehicleUI();
    this.urlManager = new CollectionVehicleURL();
  }

  /**
   * Setup the display based on current vehicle data
   */
  setupDisplay() {
    if (!this.initialized && !this.checkDependencies()) return;

    const vehicleData = this.dataManager.getVehicleData();
    
    // Setup UI with callbacks
    const callbacks = {
      onSearchAnother: () => this.handleSearchAnother(),
      onClearFilter: () => this.handleClearFilter()
    };
    
    this.uiManager.updateAllContainers(vehicleData, callbacks);
  }

  /**
   * Attach global event listeners for vehicle data changes
   */
  attachEventListeners() {
    // Listen for vehicle selection changes
    this.dataManager.onDataChange((vehicleData) => {
      // Check if default search form is currently visible
      // If so, don't update containers to avoid interfering with user search
      const defaultFormsVisible = document.querySelectorAll('.collection-easysearch-custom .title-easysearch');
      let hasVisibleDefaultForm = false;
      
      defaultFormsVisible.forEach(form => {
        if (form.style.display !== 'none') {
          hasVisibleDefaultForm = true;
        }
      });
      
      // Only update containers if user is not actively searching
      if (!hasVisibleDefaultForm) {
        const callbacks = {
          onSearchAnother: () => this.handleSearchAnother(),
          onClearFilter: () => this.handleClearFilter()
        };
        
        this.uiManager.updateAllContainers(vehicleData, callbacks);
      }
    });

    // Listen for DOM ready state changes
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupDisplay());
    }
  }

  /**
   * Handle "Search For Another Vehicle" button click
   */
  handleSearchAnother() {
    console.log('🔄 Showing search form');
    
    // Clear cache to prevent stale data from showing
    this.dataManager.invalidateCache();
    
    const containers = this.uiManager.findContainers();
    containers.forEach(container => {
      this.uiManager.showDefaultDisplay(container);
    });
  }

  /**
   * Clear search input fields (name="q")
   */
  clearSearchInput() {
    try {
      const searchInputs = document.querySelectorAll('input[name="q"]');
      searchInputs.forEach(input => {
        if (input.type !== 'hidden') {
          input.value = '';
        }
      });
      console.log('🗑️ Cleared search input fields');
    } catch (error) {
      console.warn('Error clearing search input:', error);
    }
  }

  /**
   * Handle "Clear Vehicle Filter" button click
   */
  handleClearFilter() {
    console.log('🗑️ Clearing vehicle filter');

    // Clear search input fields
    this.clearSearchInput();

    // Set flag to open modal after page reload
    try {
      localStorage.setItem('collection-vehicle-open-modal-after-clear', 'true');
    } catch (error) {
      console.warn('Failed to set modal open flag:', error);
    }

    // Clear vehicle data
    this.dataManager.clearVehicleData();

    // Check if we're on a search results page
    if (this.urlManager.isSearchPage()) {
      // For search pages, redirect to homepage AND open modal
      if (typeof window !== 'undefined') {
        window.location.href = '/?open_vehicle_modal=true';
      }
    } else {
      // For collection pages, navigate to clean URL (remove vehicle filters only)
      this.urlManager.navigateToCleanUrl();
    }
  }

  /**
   * Get current vehicle data
   */
  getVehicleData() {
    return this.dataManager ? this.dataManager.getVehicleData() : null;
  }

  /**
   * Check if display has valid vehicle data
   */
  hasValidVehicleData() {
    return this.dataManager ? this.dataManager.hasValidVehicleData() : false;
  }

  /**
   * Get vehicle display text
   */
  getVehicleDisplayText() {
    return this.dataManager ? this.dataManager.getVehicleDisplayText() : null;
  }

  /**
   * Force refresh of display
   */
  refresh() {
    if (this.dataManager) {
      this.dataManager.invalidateCache();
    }
    this.setupDisplay();
  }

  /**
   * Get debug information for all modules
   */
  getDebugInfo() {
    return {
      initialized: this.initialized,
      dependenciesLoaded: this.checkDependencies(),
      dataManager: this.dataManager?.getDebugInfo() || null,
      uiManager: this.uiManager?.getDebugInfo() || null,
      urlManager: this.urlManager?.getDebugInfo() || null
    };
  }

  /**
   * Check if modal should be opened after clear filter action
   */
  checkAndOpenModalAfterClear() {
    try {
      const shouldOpenModal = localStorage.getItem('collection-vehicle-open-modal-after-clear');

      if (shouldOpenModal === 'true') {
        console.log('🔄 Auto-opening vehicle selector modal after clear filter');

        // Remove the flag immediately to prevent repeated opens
        localStorage.removeItem('collection-vehicle-open-modal-after-clear');

        // Wait for modal manager to be ready, then open the modal
        const openModalWhenReady = () => {
          if (window.cgFinderModalManager) {
            // Find the appropriate modal to open
            const modal = window.cgFinderModalManager.modal ||
                         document.querySelector('.vehicle-selector-modal:not(.vehicle-selector-modal--logged-in)');

            if (modal) {
              // Small delay to ensure page is fully loaded
              setTimeout(() => {
                window.cgFinderModalManager.openModal(modal);
              }, 300);
            } else {
              console.warn('Vehicle selector modal not found');
            }
          } else {
            // Modal manager not ready yet, retry after a short delay
            setTimeout(openModalWhenReady, 100);
          }
        };

        openModalWhenReady();
      }
    } catch (error) {
      console.warn('Error checking modal open flag:', error);
    }
  }

  /**
   * Clean up all modules and event listeners
   */
  cleanup() {
    if (this.uiManager) {
      this.uiManager.cleanup();
    }

    if (this.dataManager) {
      this.dataManager.invalidateCache();
    }

    this.initialized = false;
    console.log('🗑️ Collection Vehicle Display cleaned up');
  }

  /**
   * Static method to reinitialize display
   */
  static refresh() {
    if (window.collectionVehicleDisplay) {
      window.collectionVehicleDisplay.refresh();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.collectionVehicleDisplay = new CollectionVehicleDisplay();
});
